import React from 'react';
import {
  formatCurrency,
  formatDate,
  getPaymentMethodLabel,
  getPaymentTypeLabel,
  getStatusConfig,
} from '../../lib/utils';

const LOGO_URL = '/logo.png';

export const VoucherPrintTemplate = ({ voucher }) => {
  if (!voucher) return null;

  const statusConfig = getStatusConfig(voucher.status);

  return (
    <div className="print-template hidden print:block bg-white text-black" style={{ fontSize: '11px', padding: '12px 18px', maxHeight: '100vh', overflow: 'hidden' }}>
      {/* Header with Logo and Title */}
      <div className="flex items-center justify-between border-b-2 border-gray-800 pb-2 mb-2">
        <div className="flex items-center gap-2">
          <img 
            src={LOGO_URL} 
            alt="Al Arabia" 
            style={{ height: '40px', width: 'auto' }}
          />
        </div>
        <div className="text-center flex-1">
          <h1 className="text-lg font-bold text-[#6a5da4] uppercase tracking-wider">Payment Voucher</h1>
        </div>
        <div className="text-right" style={{ minWidth: '100px' }}>
          <p className="font-mono font-bold" style={{ fontSize: '13px' }}>{voucher.voucherNo || 'DRAFT'}</p>
          <p className="text-gray-600" style={{ fontSize: '9px' }}>Date: {formatDate(voucher.date)}</p>
        </div>
      </div>

      {/* Status */}
      <div className="text-center mb-2">
        <span 
          className="inline-block px-3 py-0.5 rounded-full font-semibold"
          style={{ fontSize: '9px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db' }}
        >
          Status: {statusConfig.label}
        </span>
      </div>

      {/* Two Column Layout - Voucher Info & Beneficiary */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div className="border border-gray-300 rounded p-2">
          <h3 className="font-semibold text-gray-600 uppercase border-b pb-1 mb-1" style={{ fontSize: '8px', letterSpacing: '0.5px' }}>
            Voucher Information
          </h3>
          <div className="space-y-0.5" style={{ fontSize: '10px' }}>
            <div className="flex justify-between">
              <span className="text-gray-500">Branch:</span>
              <span className="font-medium">{voucher.branch}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Department:</span>
              <span className="font-medium">{voucher.department}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Created By:</span>
              <span className="font-medium">{voucher.createdByName}</span>
            </div>
            {voucher.poNumber && (
              <div className="flex justify-between">
                <span className="text-gray-500">PO Number:</span>
                <span className="font-medium font-mono">{voucher.poNumber}</span>
              </div>
            )}
            {voucher.poAmount && (
              <div className="flex justify-between">
                <span className="text-gray-500">PO Amount:</span>
                <span className="font-medium font-mono">{formatCurrency(voucher.poAmount, voucher.currency)}</span>
              </div>
            )}
            {voucher.paymentRefNumber && (
              <div className="flex justify-between">
                <span className="text-gray-500">No of Payment:</span>
                <span className="font-medium font-mono">{voucher.paymentRefNumber}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border border-gray-300 rounded p-2">
          <h3 className="font-semibold text-gray-600 uppercase border-b pb-1 mb-1" style={{ fontSize: '8px', letterSpacing: '0.5px' }}>
            Beneficiary Details
          </h3>
          <div className="space-y-0.5" style={{ fontSize: '10px' }}>
            <div className="flex justify-between">
              <span className="text-gray-500">Name:</span>
              <span className="font-medium">{voucher.beneficiaryName}</span>
            </div>
            {voucher.beneficiaryAddress && (
              <div>
                <span className="text-gray-500">Address:</span>
                <span className="font-medium ml-1">{voucher.beneficiaryAddress}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Details - Compact Row */}
      <div className="border border-gray-300 rounded p-2 mb-2">
        <h3 className="font-semibold text-gray-600 uppercase border-b pb-1 mb-1" style={{ fontSize: '8px', letterSpacing: '0.5px' }}>
          Payment Details
        </h3>
        <div className="grid grid-cols-4 gap-2" style={{ fontSize: '10px' }}>
          <div>
            <span className="text-gray-500 block" style={{ fontSize: '8px' }}>Method</span>
            <span className="font-medium">{getPaymentMethodLabel(voucher.paymentMethod)}</span>
          </div>
          <div>
            <span className="text-gray-500 block" style={{ fontSize: '8px' }}>Type</span>
            <span className="font-medium">{getPaymentTypeLabel(voucher.paymentType)}</span>
          </div>
          <div>
            <span className="text-gray-500 block" style={{ fontSize: '8px' }}>Currency</span>
            <span className="font-medium">{voucher.currency}</span>
          </div>
          {voucher.advancePercent ? (
            <div>
              <span className="text-gray-500 block" style={{ fontSize: '8px' }}>Advance %</span>
              <span className="font-medium">{voucher.advancePercent}%</span>
            </div>
          ) : (
            <div></div>
          )}
        </div>
        
        {/* Bank Details for Transfer */}
        {voucher.paymentMethod === 'TRANSFER' && (voucher.bankName || voucher.iban) && (
          <div className="mt-1 pt-1 border-t border-gray-200 grid grid-cols-2 gap-2" style={{ fontSize: '10px' }}>
            {voucher.bankName && (
              <div>
                <span className="text-gray-500 block" style={{ fontSize: '8px' }}>Bank Name</span>
                <span className="font-medium">{voucher.bankName}</span>
              </div>
            )}
            {voucher.iban && (
              <div>
                <span className="text-gray-500 block" style={{ fontSize: '8px' }}>IBAN</span>
                <span className="font-medium font-mono" style={{ fontSize: '9px' }}>{voucher.iban}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Line Items Table - Compact */}
      <div className="mb-2">
        <h3 className="font-semibold text-gray-600 uppercase mb-1" style={{ fontSize: '8px', letterSpacing: '0.5px' }}>
          Line Items
        </h3>
        <table className="w-full border-collapse" style={{ fontSize: '10px' }}>
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-2 py-1 text-left font-semibold" style={{ width: '25px' }}>#</th>
              <th className="border border-gray-300 px-2 py-1 text-left font-semibold">Document No.</th>
              <th className="border border-gray-300 px-2 py-1 text-left font-semibold">Description</th>
              <th className="border border-gray-300 px-2 py-1 text-right font-semibold" style={{ width: '80px' }}>Invoice Amount</th>
              <th className="border border-gray-300 px-2 py-1 text-right font-semibold" style={{ width: '80px' }}>Payable Amount</th>
            </tr>
          </thead>
          <tbody>
            {voucher.items?.map((item, index) => (
              <tr key={item.id || index}>
                <td className="border border-gray-300 px-2 py-0.5">{index + 1}</td>
                <td className="border border-gray-300 px-2 py-0.5 font-mono">{item.invoiceNo || '-'}</td>
                <td className="border border-gray-300 px-2 py-0.5">{item.note || '-'}</td>
                <td className="border border-gray-300 px-2 py-0.5 text-right font-mono">
                  {formatCurrency(item.amount, item.currency || voucher.currency)}
                </td>
                <td className="border border-gray-300 px-2 py-0.5 text-right font-mono">
                  {formatCurrency(item.payableAmount || 0, item.currency || voucher.currency)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* Blank line before Total Amount */}
            <tr>
              <td colSpan="5" className="border border-gray-300 px-2 py-2"></td>
            </tr>
            <tr className="bg-gray-100 font-bold">
              <td colSpan="4" className="border border-gray-300 px-2 py-1 text-right">
                Total Payable Amount:
              </td>
              <td className="border border-gray-300 px-2 py-1 text-right font-mono" style={{ fontSize: '11px' }}>
                {formatCurrency(voucher.amountTotal, voucher.currency)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Amount in Words */}
      {voucher.amountWords && (
        <div className="mb-2 p-1.5 bg-gray-50 border border-gray-300 rounded" style={{ fontSize: '9px' }}>
          <span className="text-gray-500">Amount in Words: </span>
          <span className="font-medium">{voucher.amountWords}</span>
        </div>
      )}

      {/* Signature Section - Dynamic columns based on additional manager */}
      <div className={`grid ${voucher.additionalManagerId ? 'grid-cols-5' : 'grid-cols-4'} gap-2 mt-4`}>
        <div className="text-center">
          <div className="border-t-2 border-gray-400 pt-1 mt-8">
            <p className="font-semibold" style={{ fontSize: '9px' }}>Prepared By</p>
            <p className="text-gray-600" style={{ fontSize: '8px' }}>{voucher.createdByName}</p>
            <p className="text-gray-500" style={{ fontSize: '7px' }}>{voucher.createdAt ? formatDate(voucher.createdAt) : ''}</p>
          </div>
        </div>
        <div className="text-center">
          <div className="border-t-2 border-gray-400 pt-1 mt-8">
            <p className="font-semibold" style={{ fontSize: '9px' }}>Approved By (Manager)</p>
            <p className="text-gray-600" style={{ fontSize: '8px' }}>{voucher.managerApprovedByName || '________________'}</p>
            <p className="text-gray-500" style={{ fontSize: '7px' }}>{voucher.managerActionAt ? formatDate(voucher.managerActionAt) : ''}</p>
          </div>
        </div>
        {voucher.additionalManagerId && (
          <div className="text-center">
            <div className="border-t-2 border-gray-400 pt-1 mt-8">
              <p className="font-semibold" style={{ fontSize: '9px' }}>Additional Manager</p>
              <p className="text-gray-600" style={{ fontSize: '8px' }}>{voucher.additionalManagerApprovedByName || '________________'}</p>
              <p className="text-gray-500" style={{ fontSize: '7px' }}>{voucher.additionalManagerActionAt ? formatDate(voucher.additionalManagerActionAt) : ''}</p>
            </div>
          </div>
        )}
        <div className="text-center">
          <div className="border-t-2 border-gray-400 pt-1 mt-8">
            <p className="font-semibold" style={{ fontSize: '9px' }}>Reviewed By</p>
            <p className="text-gray-600" style={{ fontSize: '8px' }}>{voucher.internalAuditApprovedByName || '________________'}</p>
            <p className="text-gray-500" style={{ fontSize: '7px' }}>{voucher.internalAuditActionAt ? formatDate(voucher.internalAuditActionAt) : ''}</p>
          </div>
        </div>
        <div className="text-center">
          <div className="border-t-2 border-gray-400 pt-1 mt-8">
            <p className="font-semibold" style={{ fontSize: '9px' }}>Approved By (Accounts)</p>
            <p className="text-gray-600" style={{ fontSize: '8px' }}>{voucher.accountsApprovedByName || '________________'}</p>
            <p className="text-gray-500" style={{ fontSize: '7px' }}>{voucher.accountsActionAt ? formatDate(voucher.accountsActionAt) : ''}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherPrintTemplate;
